import type {
  BridgePort,
  AppToHostMessage,
  HostToAppMessage,
  HostSDKError,
  Result,
  HostDirectoryService,
} from './types.js';
import type { BroadcastService } from './broadcast-service.js';
import type { InviteService } from './invite-service.js';
import { createBroadcastDescriptor } from './directory-service.js';

export interface Capabilities {
  publish: string[];
  subscribe: string[];
}

export class AppHostContainerBridge {
  private activeSubscriptions = new Map<string, () => void>();
  private isHandshakeComplete = false;
  private readonly allowedCapabilities: Capabilities = { publish: ['*'], subscribe: ['*'] };
  private readonly registeredBroadcasts = new Set<string>();
  private cleanupPortOnClose?: () => void;
  private cleanupPortOnMessage?: () => void;

  constructor(
    private readonly appId: string,
    private readonly port: BridgePort,
    private readonly broadcast: BroadcastService,
    private readonly invite: InviteService,
    private readonly directory: HostDirectoryService,
    grantedCapabilities?: Capabilities
  ) {
    if (grantedCapabilities) {
      this.allowedCapabilities = grantedCapabilities;
    }

    this.setupPortListeners();
  }

  private setupPortListeners(): void {
    this.cleanupPortOnMessage = this.port.onMessage((raw) => {
      void this.handleIncomingMessage(raw);
    });

    this.cleanupPortOnClose = this.port.onClose(() => {
      this.teardown();
    });
  }

  private isUriAllowed(uri: string, patterns: string[]): boolean {
    return patterns.some((pattern) => {
      if (pattern === '*') return true;
      if (pattern.endsWith('*')) {
        return uri.startsWith(pattern.slice(0, -1));
      }
      return uri === pattern;
    });
  }

  private postMessage(message: HostToAppMessage): void {
    this.port.postMessage(message);
  }

  async handleIncomingMessage(raw: unknown): Promise<void> {
    if (typeof raw !== 'object' || raw === null) {
      this.postMessage({ v: 1, type: 'ERROR', code: 'ERR_INVALID_FRAME', message: 'Malformed frame: expected JSON object.' });
      return;
    }

    const msg = raw as Record<string, unknown>;

    // 1. Mandatory Framing & Versioning Check (v: 1)
    if (msg.v !== 1) {
      this.postMessage({ v: 1, type: 'ERROR', code: 'ERR_UNSUPPORTED_VERSION', message: 'Unsupported protocol version tag. Expected v: 1.' });
      return;
    }

    const type = msg.type as string;
    const requestId = typeof msg.requestId === 'string' ? msg.requestId : '';

    if (!requestId && type !== 'UNSUBSCRIBE') {
      this.postMessage({ v: 1, type: 'ERROR', code: 'ERR_MISSING_REQUEST_ID', message: 'Mandatory requestId is missing from request frame.' });
      return;
    }

    // 2. Handshake Phase (APP_READY -> HOST_READY)
    if (type === 'APP_READY') {
      this.isHandshakeComplete = true;
      this.postMessage({
        v: 1,
        type: 'HOST_READY',
        requestId,
        capabilities: this.allowedCapabilities,
      });
      return;
    }

    if (!this.isHandshakeComplete) {
      this.postMessage({
        v: 1,
        type: 'COMMAND_RESULT',
        requestId,
        result: {
          ok: false,
          error: {
            type: 'BRIDGE_ERROR',
            code: 'ERR_NOT_READY',
            message: 'Bridge handshake incomplete. App must send APP_READY first.',
          },
        },
      });
      return;
    }

    // 3. Process Bridge Commands
    switch (type) {
      case 'PUBLISH': {
        const destUri = msg.destinationUri as string;
        if (!this.isUriAllowed(destUri, this.allowedCapabilities.publish)) {
          this.postMessage({
            v: 1,
            type: 'COMMAND_RESULT',
            requestId,
            result: {
              ok: false,
              error: {
                type: 'NOT_AUTHORIZED',
                code: 'ERR_NOT_AUTHORIZED',
                message: `Publish to URI '${destUri}' rejected by capability policy.`,
              },
            },
          });
          return;
        }

        const res = await this.broadcast.publish(destUri, msg.payload);
        this.postMessage({ v: 1, type: 'COMMAND_RESULT', requestId, result: res });
        break;
      }

      case 'SUBSCRIBE': {
        const destUri = msg.destinationUri as string;
        const subId = msg.subscriptionId as string;

        if (!this.isUriAllowed(destUri, this.allowedCapabilities.subscribe)) {
          this.postMessage({
            v: 1,
            type: 'COMMAND_RESULT',
            requestId,
            result: {
              ok: false,
              error: {
                type: 'NOT_AUTHORIZED',
                code: 'ERR_NOT_AUTHORIZED',
                message: `Subscribe to URI '${destUri}' rejected by capability policy.`,
              },
            },
          });
          return;
        }

        // Setup channel subscription
        const unsubscribe = this.broadcast.subscribe(destUri, (envelope) => {
          this.postMessage({
            v: 1,
            type: 'BROADCAST_EVENT',
            subscriptionId: subId,
            envelope,
          });
        });

        this.activeSubscriptions.set(subId, unsubscribe);
        this.postMessage({ v: 1, type: 'SUBSCRIBE_ACK', requestId, subscriptionId: subId });
        break;
      }

      case 'UNSUBSCRIBE': {
        const subId = msg.subscriptionId as string;
        const unsub = this.activeSubscriptions.get(subId);
        if (unsub) {
          unsub();
          this.activeSubscriptions.delete(subId);
        }
        if (requestId) {
          this.postMessage({ v: 1, type: 'COMMAND_RESULT', requestId, result: { ok: true, value: undefined } });
        }
        break;
      }

      case 'SEND_INVITE': {
        const res = await this.invite.sendInvite({
          targetNodeId: msg.targetNodeId as string,
          channelUri: msg.channelUri as string,
          metadata: msg.metadata,
        });
        this.postMessage({ v: 1, type: 'COMMAND_RESULT', requestId, result: res });
        break;
      }

      case 'ACCEPT_INVITE': {
        const res = await this.invite.acceptInvite(msg.inviteId as string);
        this.postMessage({ v: 1, type: 'COMMAND_RESULT', requestId, result: res });
        break;
      }

      case 'REJECT_INVITE': {
        const res = await this.invite.rejectInvite(msg.inviteId as string);
        this.postMessage({ v: 1, type: 'COMMAND_RESULT', requestId, result: res });
        break;
      }

      case 'REGISTER_BROADCAST': {
        const kind = msg.kind as any;
        const validKinds = ['video', 'audio', 'screenShare', 'whiteboard', 'broadcast'];
        if (!validKinds.includes(kind)) {
          this.postMessage({
            v: 1,
            type: 'COMMAND_RESULT',
            requestId,
            result: {
              ok: false,
              error: { type: 'INVALID_KIND', code: 'ERR_INVALID_KIND', message: `Invalid broadcast kind '${kind}'` },
            },
          });
          return;
        }

        const descriptor = createBroadcastDescriptor({
          nodeId: this.directory.getLocalNodeId(),
          kind,
          topic: msg.topic as string | undefined,
        });

        // Namespace the broadcast for the sandbox app
        const namespacedUri = `hts://${this.directory.getLocalNodeId()}.mesh/app/${this.appId}/${kind}`;
        
        try {
          await this.directory.registerBroadcast({ ...descriptor, uri: namespacedUri });
          this.registeredBroadcasts.add(namespacedUri);
          this.postMessage({ v: 1, type: 'COMMAND_RESULT', requestId, result: { ok: true, value: undefined } });
        } catch (e: any) {
          this.postMessage({
            v: 1,
            type: 'COMMAND_RESULT',
            requestId,
            result: { ok: false, error: { type: 'DIRECTORY_ERROR', code: 'ERR_DIRECTORY', message: e.message } },
          });
        }
        break;
      }

      case 'UNREGISTER_BROADCAST': {
        const kind = msg.kind as any;
        const namespacedUri = `hts://${this.directory.getLocalNodeId()}.mesh/app/${this.appId}/${kind}`;
        try {
          await this.directory.unregisterBroadcast(namespacedUri);
          this.registeredBroadcasts.delete(namespacedUri);
          this.postMessage({ v: 1, type: 'COMMAND_RESULT', requestId, result: { ok: true, value: undefined } });
        } catch (e: any) {
          this.postMessage({
            v: 1,
            type: 'COMMAND_RESULT',
            requestId,
            result: { ok: false, error: { type: 'DIRECTORY_ERROR', code: 'ERR_DIRECTORY', message: e.message } },
          });
        }
        break;
      }

      default: {
        this.postMessage({
          v: 1,
          type: 'COMMAND_RESULT',
          requestId,
          result: {
            ok: false,
            error: {
              type: 'BRIDGE_ERROR',
              code: 'ERR_UNKNOWN_COMMAND',
              message: `Unknown command type '${type}'.`,
            },
          },
        });
      }
    }
  }

  teardown(): void {
    for (const unsubscribe of this.activeSubscriptions.values()) {
      try {
        unsubscribe();
      } catch {
        // Safe disposal
      }
    }
    this.activeSubscriptions.clear();

    for (const uri of this.registeredBroadcasts) {
      try {
        this.directory.unregisterBroadcast(uri);
      } catch (err) {
        console.warn('[AppBridge] failed to unregister broadcast on teardown', uri, err);
      }
    }
    this.registeredBroadcasts.clear();

    if (this.cleanupPortOnMessage) {
      this.cleanupPortOnMessage();
    }
    if (this.cleanupPortOnClose) {
      this.cleanupPortOnClose();
    }
  }
}
